/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../components/ui/page-header.js';

import { ParentInvoiceList } from './billing-invoice-list.js';
import { ParentProformasPanel } from './proformas-panel.js';

type Tab = 'proformas' | 'invoices';
const TABS: readonly Tab[] = ['proformas', 'invoices'];

/**
 * Parent → Billing (Figma). A Proformas / Invoices segmented toggle over simple
 * card rows: proformas to approve/decline (CR-008), invoices to view + pay.
 */
export function ParentBillingPage(): JSX.Element {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('proformas');
  const select = useCallback(
    (next: Tab) => () => {
      setTab(next);
    },
    [],
  );

  return (
    <div className="p-8">
      <PageHeader title={t('parent.billing.title')} />
      <div className="mb-6 inline-flex rounded-xl bg-slate-100 p-1 dark:bg-slate-800">
        {TABS.map((value) => (
          <button
            key={value}
            type="button"
            onClick={select(value)}
            className={`focus-ring rounded-lg px-5 py-2 text-sm font-medium ${
              tab === value ? 'bg-[var(--accent)] text-white' : 'text-slate-600 dark:text-slate-300'
            }`}
          >
            {t(`parent.billing.tab_${value}`)}
          </button>
        ))}
      </div>
      {tab === 'proformas' ? <ParentProformasPanel /> : <ParentInvoiceList />}
    </div>
  );
}
